import axiosBlobInstance from './axiosBlobInstance';
import axiosInstance from './axiosInstance';
import { DocumentActionDto, DocumentUpdateMetadataDto } from '@docbay/schemas';

/**
 * An object containing methods for interacting with appointment documents.
 */
export const AppointmentDocumentsApi = {
  /**
   * Uploads a new document to the appointment.
   *
   * @param data - The form data containing the document to upload.
   * @returns A promise that resolves when the document is uploaded.
   */
  postDocuments: (data: FormData) =>
    axiosInstance.post('/appointment-documents', data),

  /**
   * Uploads a new document to the specified appointment.
   *
   * @param params - An object containing the appointment ID and form data for the document to upload.
   * @returns A promise that resolves when the document is uploaded.
   */
  postDocumentsToAppointment: (params: {
    appointmentId: string;
    data: FormData;
  }) =>
    axiosInstance.post(
      `/appointment-documents/${params.appointmentId}/upload`,
      params.data,
    ),

  /**
   * Downloads a document from the appointment.
   *
   * @param params - An object containing the bucket type and key of the document to download.
   * @returns A promise that resolves with the downloaded document.
   */
  downloadDocuments: (params: { bucketType: string; key: string }) =>
    axiosInstance.get(
      `/appointment-documents?bucketType=${params.bucketType}&key=${params.key}`,
    ),

  /**
   * Downloads a specific document from the appointment.
   *
   * @param params - An object containing the related entity ID, bucket type, and key of the document to download.
   * @returns A promise that resolves with the downloaded document.
   */
  downloadDocument: (params: DocumentActionDto) => {
    const { relatedEntityId, bucketType, key } = params;
    return axiosBlobInstance.get('/appointment-documents/download', {
      params: {
        ...(relatedEntityId ? { relatedEntityId } : {}),
        bucketType,
        key,
      },
    });
  },

  /**
   * Deletes a document from the appointment.
   *
   * @param params - An object containing the related entity ID, bucket type, and key of the document to delete.
   * @returns A promise that resolves when the document is deleted.
   */
  deleteDocuments: (params: {
    relatedEntityId?: string;
    bucketType: string;
    key: string;
  }) =>
    axiosInstance.delete(
      `/appointment-documents?relatedEntityId=${
        params.relatedEntityId ? params.relatedEntityId : ''
      }&bucketType=${params.bucketType}&key=${params.key}`,
    ),

  /**
   * Updates the metadata of a document in the appointment.
   *
   * @param params - An object containing the updated metadata for the document.
   * @returns A promise that resolves when the metadata is updated.
   */
  editDocuments: (params: DocumentUpdateMetadataDto) =>
    axiosInstance.patch('/appointment-documents', params),

  /**
   * Downloads an invoice from the appointment.
   *
   * @param documentId - The ID of the document to download.
   * @returns A promise that resolves with the downloaded invoice.
   */
  downloadInvoice: (documentId: string) =>
    axiosBlobInstance.get(
      `/appointment-documents/download-invoice/${documentId}`,
    ),
};
